import { Batch, BatchAnalysis, BatchSchedule } from '@/types';
import { BrewfatherBatch, BrewfatherService } from './BrewfatherService';

const baseUrl =
	(import.meta.env.VITE_API_BASE_URL as string | undefined) ||
	'https://r447k5srii.execute-api.ap-southeast-2.amazonaws.com/dev';

async function http<T>(path: string, options: RequestInit, authHeader: Record<string, string>): Promise<T> {
	const res = await fetch(`${baseUrl}${path}`, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			...authHeader,
			...(options.headers || {})
		}
	});
	if (!res.ok) {
		const contentType = res.headers.get('content-type') || '';
		try {
			if (contentType.includes('application/json')) {
				const data = (await res.json()) as any;
				const msg =
					(typeof data?.message === 'string' && data.message) ||
					(typeof data?.error === 'string' && data.error) ||
					JSON.stringify(data);
				throw new Error(msg || `Request failed: ${res.status}`);
			} else {
				const text = await res.text();
				throw new Error(text || `Request failed: ${res.status}`);
			}
		} catch (e: any) {
			throw new Error(e?.message || `Request failed: ${res.status}`);
		}
	}
	return (await res.json()) as T;
}

// Test mode flag - set to true to use mock data
const USE_TEST_DATA = false;

// Test batch data
const TEST_BATCH_DATA: any = {
	"_id": "bCKtUVUUJhqyiw6N3zCJJj88KHlCvv",
	"_archived": false,
	"name": "Batch",
	"brewer": "Luiz Felipe Puccinelli Glingani",
	"status": "Fermenting",
	"batchNo": 1,
	"brewDate": 1769004000000,
	"bottlingDate": 1770321589599,
	"estimatedOg": 1.056,
	"estimatedFg": 1.009,
	"estimatedIbu": 50,
	"estimatedColor": 7.3,
	"measuredOg": 1.056,
	"measuredFg": null,
	"measuredAbv": 6.2,
	"measuredIbu": null,
	"measuredColor": null,
	"measuredAttenuation": 83.3,
	"measuredEfficiency": 70.16,
	"recipe": {
		"_id": "8VRfVXnXgjbFEe1dq0DhaEVxm9Fe0X",
		"name": "WC IPA - Coastbusters - BZ35L",
		"fermentation": {
			"steps": [
				{
					"stepTemp": 20,
					"actualTime": 1769090400000,
					"stepTime": 14,
					"type": "Primary"
				}
			]
		}
	},
	"notes": [
		{
			"timestamp": 1769286376948,
			"note": "Dry hop",
			"status": "Fermenting"
		},
		{
			"note": "",
			"status": "Fermenting",
			"type": "statusChanged",
			"timestamp": 1769111989233
		}
	],
	"events": [
		{
			"time": 1769040000000,
			"notifyTime": 1769040000000,
			"eventType": "event-batch-brew-day",
			"eventText": "Brew Day",
			"title": "Brew Day - Batch #1 (WC IPA - Coastbusters - BZ35L)",
			"description": "Brew Day (WC IPA - Coastbusters - BZ35L)",
			"active": false,
			"dayEvent": true
		},
		{
			"time": 1770336000000,
			"eventType": "event-batch-bottling-day",
			"eventText": "Bottling Day",
			"title": "Bottling Day - Batch #1 (WC IPA - Coastbusters - BZ35L)",
			"description": "Bottling Day (WC IPA - Coastbusters - BZ35L)",
			"active": true,
			"dayEvent": true
		},
		{
			"dayEvent": true,
			"time": 1770163200000,
			"eventType": "event-batch-dry-hop",
			"active": true,
			"title": "Dry Hop - Batch #1 (WC IPA - Coastbusters - BZ35L)",
			"description": "Amarillo (50 g), Simcoe (50 g)",
			"eventText": null
		},
		{
			"time": 1769126400000,
			"eventType": "event-batch-ferm-step",
			"eventText": "Primary (Fermentation) @ 20 °C",
			"title": "Fermentation Step - Batch #1 (WC IPA - Coastbusters - BZ35L)",
			"description": "Fermentation Profile Step: Primary (Fermentation) @ 20 °C",
			"active": true,
			"dayEvent": false
		}
	]
};

const mapBrewfatherStatus = (status: string): Batch['status'] => {
	const statusMap: Record<string, Batch['status']> = {
		'Planning': 'planned',
		'Brewing': 'brewing',
		'Fermenting': 'fermenting',
		'Conditioning': 'conditioning',
		'Completed': 'completed',
		'Archived': 'archived'
	};
	return statusMap[status] || 'planned';
};

const formatDate = (date: number | string | undefined): string | undefined => {
	if (!date) return undefined;
	if (typeof date === 'string') return date;
	// Convert timestamp to ISO string
	return new Date(date).toISOString().split('T')[0];
};

const formatNotes = (notes: string | Array<{ timestamp: number; note: string; status?: string; type?: string }> | undefined): Array<{ timestamp: number; note: string; status?: string }> | undefined => {
	if (!notes) return undefined;
	if (typeof notes === 'string') {
		return [{ timestamp: Date.now(), note: notes }];
	}
	if (Array.isArray(notes)) {
		return notes.filter(n => n.note && n.note.trim() && n.type !== 'statusChanged');
	}
	return undefined;
};

const calculateExpectedFermentationDays = (recipe: any): number | undefined => {
	if (!recipe?.fermentation?.steps) return undefined;
	// Sum up all fermentation step times
	const totalDays = recipe.fermentation.steps.reduce((sum: number, step: any) => {
		return sum + (step.stepTime || 0);
	}, 0);
	return totalDays > 0 ? totalDays : undefined;
};

const mapBrewfatherBatchToBatch = (bfBatch: any, userId: string): Batch => {
	const expectedFermentationDays = calculateExpectedFermentationDays(bfBatch.recipe);
	
	return {
		id: bfBatch._id,
		name: bfBatch.name || `Batch ${bfBatch.batchNo || ''}`,
		recipeId: bfBatch.recipe?._id,
		recipeName: bfBatch.recipe?.name,
		status: mapBrewfatherStatus(bfBatch.status),
		brewDate: formatDate(bfBatch.brewDate),
		bottlingDate: formatDate(bfBatch.bottlingDate),
		fermentationStartDate: bfBatch.fermentationStartDate ?? undefined,
		expectedFermentationDays,
		originalGravity: bfBatch.measuredOg ?? undefined,
		finalGravity: bfBatch.measuredFg ?? undefined,
		abv: bfBatch.measuredAbv ?? undefined,
		ibu: bfBatch.measuredIbu ?? undefined,
		srm: bfBatch.measuredColor ?? undefined,
		estimatedOg: bfBatch.estimatedOg ?? undefined,
		estimatedFg: bfBatch.estimatedFg ?? undefined,
		estimatedAbv: bfBatch.recipe?.abv ?? undefined,
		estimatedIbu: bfBatch.estimatedIbu ?? undefined,
		estimatedColor: bfBatch.estimatedColor ?? undefined,
		measuredAttenuation: bfBatch.measuredAttenuation ?? undefined,
		measuredEfficiency: bfBatch.measuredEfficiency ?? undefined,
		notes: formatNotes(bfBatch.notes),
		events: bfBatch.events || [],
		brewfatherBatchId: bfBatch._id,
		userId,
		createdAt: typeof bfBatch.brewDate === 'number' 
			? new Date(bfBatch.brewDate).toISOString() 
			: bfBatch.brewDate || new Date().toISOString(),
		updatedAt: new Date().toISOString()
	};
};

export const BatchService = {
	listBatches: async (userId: string, authHeader: Record<string, string>): Promise<{ items: Batch[] }> => {
		if (USE_TEST_DATA) {
			// Return test data for list (simplified)
			return { items: [mapBrewfatherBatchToBatch(TEST_BATCH_DATA, userId)] };
		}
		const brewfatherBatches = await BrewfatherService.getBatches(userId, authHeader);
		const batches = brewfatherBatches.map(bf => mapBrewfatherBatchToBatch(bf, userId));
		return { items: batches };
	},

	getBatch: async (batchId: string, userId: string, authHeader: Record<string, string>): Promise<{ item: Batch }> => {
		if (USE_TEST_DATA) {
			// Return full test data
			return { item: mapBrewfatherBatchToBatch(TEST_BATCH_DATA, userId) };
		}
		const bfBatch = await BrewfatherService.getBatch(batchId, userId, authHeader);
		const batch = mapBrewfatherBatchToBatch(bfBatch, userId);
		
		// Fetch all readings if available
		try {
			const readings = await BrewfatherService.getBatchReadings(batchId, userId, authHeader);
			if (readings && readings.length > 0) {
				// Map all readings
				batch.readings = readings.map((reading) => ({
					temp: reading.temp,
					sg: reading.sg,
					time: reading.time,
					battery: reading.battery,
					rssi: reading.rssi,
					type: reading.type
				}));
				
				// Set lastReading for backward compatibility
				const lastReading = readings[0];
				batch.lastReading = {
					temp: lastReading.temp,
					sg: lastReading.sg,
					time: lastReading.time,
					battery: lastReading.battery,
					rssi: lastReading.rssi,
					type: lastReading.type
				};
			}
		} catch (error) {
			// Silently fail if readings are not available
			console.warn('Could not fetch readings:', error);
		}
		
		return { item: batch };
	},

	getBatchFromBackend: async (batchId: string, userId: string, authHeader: Record<string, string>): Promise<{ item: Batch }> => {
		return await http<{ item: Batch }>(`/batches/${userId}/${batchId}`, {
			method: 'GET'
		}, authHeader);
	},

	analyzeBatch: async (batchId: string, userId: string, authHeader: Record<string, string>): Promise<{ 
		analysisId?: string;
		createdAt?: string;
		summary: string; 
		recommendations?: string[];
		changes?: Array<{
			field: string;
			oldValue: any;
			newValue: any;
			reason: string;
		}>;
	}> => {
		return await http<{ 
			analysisId?: string;
			createdAt?: string;
			summary: string; 
			recommendations?: string[];
			changes?: Array<{
				field: string;
				oldValue: any;
				newValue: any;
				reason: string;
			}>;
		}>(`/batches/${userId}/${batchId}/analyze`, {
			method: 'POST'
		}, authHeader);
	},

	listAnalyses: async (batchId: string, userId: string, authHeader: Record<string, string>): Promise<{ items: BatchAnalysis[] }> => {
		return await http<{ items: BatchAnalysis[] }>(`/batches/${userId}/${batchId}/analyses`, {
			method: 'GET'
		}, authHeader);
	},

	createSchedule: async (batchId: string, userId: string, interval: number, enabled: boolean, authHeader: Record<string, string>): Promise<{ schedule: BatchSchedule }> => {
		return await http<{ schedule: BatchSchedule }>(`/batches/${userId}/${batchId}/schedule`, {
			method: 'POST',
			body: JSON.stringify({ interval, enabled })
		}, authHeader);
	},

	updateSchedule: async (batchId: string, userId: string, interval?: number, enabled?: boolean, authHeader?: Record<string, string>): Promise<{ schedule: BatchSchedule }> => {
		return await http<{ schedule: BatchSchedule }>(`/batches/${userId}/${batchId}/schedule`, {
			method: 'PUT',
			body: JSON.stringify({ interval, enabled })
		}, authHeader || {});
	},

	deleteSchedule: async (batchId: string, userId: string, authHeader: Record<string, string>): Promise<{ message: string }> => {
		return await http<{ message: string }>(`/batches/${userId}/${batchId}/schedule`, {
			method: 'DELETE'
		}, authHeader);
	},

	getSchedule: async (batchId: string, userId: string, authHeader: Record<string, string>): Promise<{ schedule: BatchSchedule | null }> => {
		return await http<{ schedule: BatchSchedule | null }>(`/batches/${userId}/${batchId}/schedule`, {
			method: 'GET'
		}, authHeader);
	}
};
